import * as THREE from 'three'
import Sizes from "./Utils/Sizes.js"
import Time from "./Utils/Time.js"
import Camera from "./Camera.js"
import Renderer from './Renderer.js'
import World from './World/World.js'
import Debug from './Utils/Debug.js'
import PhysicsWorld from './PhysicsWorld.js'
import GameSettings from './GameSettings.js'
import { KartNet } from './Net/KartNet.js'
import { TouchControls } from './Net/TouchControls.js'
import Race from './Race/Race.js'
import Items from './Items/Items.js'

let instance = null

export default class Experience {
    constructor(canvas) {
        // Singleton
        if (instance) {
            return instance
        }

        instance = this

        // Options
        this.canvas = canvas

        // Setup
        this.debug = new Debug()
        this.sizes = new Sizes()
        this.time = new Time()
        this.scene = new THREE.Scene()
        this.worldPhysics = new PhysicsWorld()
        this.camera = new Camera()
        this.renderer = new Renderer()

        this.world = new World()

        // Access simpleCar directly from world
        if (this.world.simpleCar) {
            this.simpleCar = this.world.simpleCar
        }
        
        // Initialize game settings GUI (after world is created)
        this.gameSettings = new GameSettings()
        this.setupSettingsCallbacks()

        // Multiplayer P2P (PeerJS) — sala criada/entrada pelo prompt do script.js.
        this.kartNet = new KartNet(this)

        // Controles de toque (aparecem sozinho em celular/tablet)
        this.touchControls = new TouchControls()

        // Corrida: lobby -> contagem -> voltas -> resultados
        this.race = new Race(this)

        // Itens (caixas, banana, cascos, turbo)
        this.items = new Items(this)

        // Catálogos modulares (carros, personagens, mapas) — runtime, sem rebuild
        this._loadContentCatalogs()


        // Sizes resize event
        this.sizes.on('resize', () => {
            this.resize()
        })

        // Time tick event
        this.time.on('tick', () => {
            this.update()
        })
    }
    
    setupSettingsCallbacks() {
        // Car color changes
        this.gameSettings.on('onCarColorChange', (color) => {
            if (this.simpleCar?.updateColor) {
                this.simpleCar.updateColor(color)
            }
        })
        
        // Camera changes
        this.gameSettings.on('onCameraChange', (settings) => {
            if (this.camera?.updateSettings) {
                this.camera.updateSettings(settings)
            }
        })
        
        // Gameplay changes
        this.gameSettings.on('onGameplayChange', (settings) => {
            if (this.world?.gameManager?.updateSettings) {
                this.world.gameManager.updateSettings(settings)
            }
        })
        
        // Appearance changes
        this.gameSettings.on('onAppearanceChange', (data) => {
            if (data.type === 'grass' && this.world?.floor?.updateGrassColor) {
                this.world.floor.updateGrassColor(data.value)
            }
            if (data.type === 'fence' && this.world?.floor?.updateFenceColor) {
                this.world.floor.updateFenceColor(data.value)
            }
            if (data.type === 'shadows') {
                this.renderer.instance.shadowMap.enabled = data.value
            }
        })
        
        // Actions
        this.gameSettings.callbacks.onAction = [(data) => {
            if (data.type === 'resetCar' && this.simpleCar?.resetPosition) {
                this.simpleCar.resetPosition()
            }
            if (data.type === 'restartGame' && this.world?.gameManager?.restartGame) {
                this.world.gameManager.restartGame()
            }
            if (data.type === 'respawnCoins' && this.world?.gameManager?.spawnCoins) {
                this.world.gameManager.spawnCoins()
            }
        }]
    }

    async _loadContentCatalogs() {
        const carCatalog = (await import('./Cars/CarCatalog.js')).default
        const characterCatalog = (await import('./Content/CharacterCatalog.js')).default
        const mapCatalog = (await import('./Content/MapCatalog.js')).default
        await Promise.all([
            carCatalog.load(),
            characterCatalog.load(),
            mapCatalog.load(),
        ])
        // aplica mapa ativo (cores/cenário); Race já usa getTrack()
        if (this.world?.floor?.applyMap) {
            this.world.floor.applyMap(mapCatalog.active)
        }
    }

    resize() {
        this.camera.resize()
        this.renderer.resize()
    }

    update() {
        this.camera.update()
        this.renderer.update()
        this.worldPhysics.update()

        if (this.simpleCar) {
            this.simpleCar.update()
        }
        
        // Update world (game manager, etc.)
        if (this.world) {
            this.world.update()
        }

        // Corrida (progresso, contagem, HUD)
        if (this.race) {
            this.race.update()
        }
        if (this.items) {
            this.items.update()
        }

        // Multiplayer: envia estado local + interpola karts remotos (dt em SEGUNDOS)
        if (this.kartNet) {
            this.kartNet.update(Math.min(this.time.delta, 100) / 1000, this.simpleCar)
        }
    }

    destroy() {
        this.sizes.off('resize')
        this.time.off('tick')

        this.scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose()

                for (const key in child.material) {
                    const value = child.material[key]

                    if (value && typeof value.dispose === 'function') {
                        value.dispose()
                    }
                }
            }
        })

        if (this.camera.controls) {
            this.camera.controls.dispose()
        }
        this.renderer.instance.dispose()

        if (this.debug.active) {
            this.debug.gui.destroy()
        }
        
        if (this.gameSettings) {
            this.gameSettings.destroy()
        }
    }
}